import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllYards } from '../services/api';
import { DEMO, useDemo } from '../components/DemoModal';
import { MATERIALS, type Yard } from '../types';

const blankYard = (): Partial<Yard> => ({
  yard_id: '',
  name: '',
  address: '',
  city: '',
  state: '',
  hardwood_mulch: false,
  topsoil: false,
  screened_sand: false,
  crushed_gravel: false,
  river_rock: false,
  sod: false,
});

export default function Yards() {
  const { showDemo } = useDemo();
  const { data } = useQuery({ queryKey: ['yards-all'], queryFn: getAllYards });
  const [draft, setDraft] = useState<Partial<Yard>>(blankYard());
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    setErr(null);
    if (!draft.yard_id || !draft.name) {
      setErr('yard id and name are required');
      return;
    }
    showDemo(DEMO.saveYard);
  };

  const yards = data?.yards ?? [];

  return (
    <div className="page-wrap">
      <h2>Yards</h2>

      <div className="card" style={{ padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Add / update a yard</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <div>
            <label className="field">Yard ID</label>
            <input className="input" value={draft.yard_id ?? ''} onChange={(e) => setDraft({ ...draft, yard_id: e.target.value })} />
          </div>
          <div>
            <label className="field">Name</label>
            <input className="input" value={draft.name ?? ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div>
            <label className="field">Address</label>
            <input className="input" value={draft.address ?? ''} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
          </div>
          <div>
            <label className="field">City</label>
            <input className="input" value={draft.city ?? ''} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
          </div>
          <div>
            <label className="field">State</label>
            <input className="input" value={draft.state ?? ''} onChange={(e) => setDraft({ ...draft, state: e.target.value })} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12 }}>
          {MATERIALS.map((m) => (
            <label key={m.key} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={!!(draft as any)[m.key]}
                onChange={(e) => setDraft({ ...draft, [m.key]: e.target.checked })}
              />
              {m.name}
            </label>
          ))}
        </div>
        {err && <p style={{ color: 'var(--danger)' }}>{err}</p>}
        <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={submit}>
          Save yard
        </button>
        <span className="muted" style={{ fontSize: 12, marginLeft: 10 }}>
          Lane distances + pricing recompute automatically after save.
        </span>
      </div>

      <table className="data-table card" style={{ display: 'table' }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>City</th>
            <th>Materials</th>
            <th>Coords</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {yards.map((y) => (
            <tr key={y.yard_id}>
              <td>{y.yard_id}</td>
              <td>{y.name}</td>
              <td>{[y.city, y.state].filter(Boolean).join(', ')}</td>
              <td>{MATERIALS.filter((m) => (y as any)[m.key]).map((m) => m.name).join(', ') || '—'}</td>
              <td>{y.lat != null && y.lon != null ? `${Number(y.lat).toFixed(3)}, ${Number(y.lon).toFixed(3)}` : '—'}</td>
              <td>
                <button className="btn btn-sm" onClick={() => setDraft(y)}>
                  Edit
                </button>{' '}
                <button className="btn btn-sm btn-danger" onClick={() => showDemo(DEMO.deleteYard)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
